import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export const getStartedCardSx = (theme) => ({
  width: '100%',
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: 2.5,
  boxShadow: `0 14px 36px ${theme.palette.common.black}0D`,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'visible',
});

export const getStartedHeaderSx = (theme, tone = 'primary') => {
  const palette = tone === 'warning' ? theme.palette.warning : theme.palette.primary;

  return {
    p: { xs: 1.75, sm: 2 },
    borderRadius: 2,
    bgcolor: alpha(palette.lighter || palette.light, 0.24),
    border: `1px solid ${alpha(palette.main, 0.16)}`,
  };
};

export const emptyChecklist = {
  org_done: false,
  org_skipped: false,
  brand_done: false,
  brand_skipped: false,
  payment_done: false,
  payment_skipped: false,
};

export const normalizeGetStartedChecklist = (checklist) => ({
  ...emptyChecklist,
  ...(checklist || {}),
});

export const isGetStartedComplete = (checklist) => {
  const normalized = normalizeGetStartedChecklist(checklist);
  return (
    (normalized.org_done || normalized.org_skipped) &&
    (normalized.brand_done || normalized.brand_skipped) &&
    (normalized.payment_done || normalized.payment_skipped)
  );
};

export const getStoredGetStartedChecklist = () => {
  const rawChecklist = localStorage.getItem('get_started');
  if (!rawChecklist) {
    return null;
  }
  try {
    return normalizeGetStartedChecklist(JSON.parse(rawChecklist));
  } catch (error) {
    localStorage.removeItem('get_started');
    return null;
  }
};

export const shouldRouteToGetStarted = (pathname = window.location.pathname) => {
  const checklist = getStoredGetStartedChecklist();
  return Boolean(checklist && pathname !== '/get-started' && !isGetStartedComplete(checklist));
};

export const StepHeader = ({ icon, title, description, done }) => {
  const { t: translate } = useTranslation();

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: done ? 'success.lighter' : 'primary.lighter',
          color: done ? 'success.main' : 'primary.main',
          flexShrink: 0,
        }}
      >
        {done ? <CheckCircleIcon /> : icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" fontWeight={900}>
            {title}
          </Typography>
          {done && (
            <Chip label={translate('get_started_page.done', { defaultValue: 'Done' })} color="success" size="small" />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Stack>
  );
};
